import DoneIcon from '@mui/icons-material/Done';
import { Paper, Tooltip } from '@mui/material';
import { DateTime } from 'luxon';
import { useCallback, useMemo, useRef, useState } from 'react';
import cookie from 'react-cookies';
import { Link as NavLink, Route, Switch } from 'react-router-dom';
import { useRecoilValueLoadable, useSetRecoilState } from 'recoil';

import Axios from '../../common/axiosInterceptor';
import { Logger } from '../../common/Logger';
import { loadableEnum } from '../../common/useMakePHPRequest';
import {
  trackAddBlockedNumber,
  trackEditNumber,
} from '../../common/userTracking';
import { user } from '../../recoil/atoms/user';
import { getBlockedNumbers } from '../../recoil/selectors/getBlockedNumbers';
import {
  methodNameEnum,
  refetchToggle,
} from '../../recoil/selectors/makePHPRequest';
import {
  endpointEnum,
  makeApiRequestCached,
} from '../../services/rest/makeAPIRequest';
import { AddBlockedNumberModal } from '../components/modals/AddBlockedNumberModal';
import { DeleteBlockedNumbersModal } from '../components/modals/DeleteBlockedNumbersModal';
import { EditBlockedNumberModal } from '../components/modals/EditBlockedNumberModal';
import { useSnackbarWithStyles } from '../components/notifications/SnackbarProviderWithStyles';
import { ActionColumn } from '../modules/Manage/pages/queues-table/ActionColumns';
import dataTableScss from '../modules/Manage/pages/queues-table/data-table.module.css';
import { DataTable } from '../modules/Manage/pages/queues-table/DataTable';
import {
  EnhancedTableToolbar,
  ToolbarAddDeleteButton,
} from '../modules/Manage/pages/queues-table/EnhancedTableToolbar';

export function BlockedNumbers() {
  const [selected, setSelected] = useState([]);
  const userAtom = useRecoilValueLoadable(user);
  const [deleteGroupId, setDeleteGroupId] = useState('');
  const [deleteStatus, setDeleteStatus] = useState(loadableEnum.EMPTY);
  // state for opening/closing delete modal
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  // state for opening/closing add new modal
  const [showA, setShowA] = useState(false);
  const handleCloseA = () => setShowA(false);
  const handleShowA = () => setShowA(true);
  const setRefetchToggleForBlockedNumbers = useSetRecoilState(
    refetchToggle('getBlockedNumbers')
  );
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortModel, setGridSortModel] = useState([
    {
      field: 'created',
      sort: 'desc',
    },
  ]);
  const [search, setSearch] = useState('');
  const [businessDataCached] = makeApiRequestCached(
    {
      endpoint: endpointEnum.GET_BUSINESS_DATA,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_DATA,
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result?.[0] ?? cached.current
  );

  const handleSearch = (val) => {
    setPage(1);
    setSearch(val);
  };

  const handleDeleteBlockedNumber = async (id) => {
    let errorMessage = '';
    setDeleteStatus(loadableEnum.LOADING);
    const data = JSON.stringify({
      businessid: userAtom.contents?.businessid,
      apikey: userAtom.contents?.apikey,
      // extract the actual number
      number: id.split(',')[0],
    });
    const url = `${process.env.VITE_APP_API_DOMAIN}/${process.env.VITE_APP_API_STAGE}/blockedNumbers/numbers`;
    const accessToken = cookie.load('accessToken');
    const response = await Axios({
      method: 'DELETE',
      url,
      headers: {
        authorization: accessToken,
      },
      data,
    });
    setDeleteStatus(loadableEnum.HAS_VALUE);
    if (response?.data?.body?.success) {
      // clear selections list
      setDeleteGroupId(undefined);
      // refetch business queues
      setRefetchToggleForBlockedNumbers({});
      // log success
      Logger.trace(
        {
          message:
            status === loadableEnum.CANCELED
              ? 'Canceled deleteBlockedNumber'
              : 'Success deleteBlockedNumber',
          response,
        },
        'BlockedNumbers:handleDeleteBlockedNumber'
      );
      setDeleteStatus(loadableEnum.HAS_VALUE);
    } else {
      errorMessage = 'Failed to delete blocked number';
      // log error
      Logger.error(
        {
          message: 'Failed deleteQueue',
          errorMessage: response?.data?.meta?.message,
        },
        'BlockedNumbers:handleDeleteBlockedNumber'
      );
      setDeleteStatus(loadableEnum.HAS_ERROR);
    }
    enqueueSnackbarWithStyles({
      message: errorMessage || 'Successfully deleted blocked number',
      alertProps: { severity: errorMessage ? 'error' : 'success' },
    });
  };
  function phoneNumberformatter(params) {
    return (
      <Tooltip title="Edit Number">
        <NavLink
          disabled={blockedData.state === 'loading'}
          // className="btn btn-icon btn-light btn-hover-danger btn-sm"
          to={`/manage/numbers/blocked/${params.row.id}/edit`}
          // onClick={() => viewAction(params.row)}
          onClick={() => trackEditNumber()}
        >
          {params.value}
        </NavLink>
      </Tooltip>
    );
  }

  const handlePageChange = (page, details) => {
    setPage(page);
  };

  const handlePageSizeChange = (pageSize, details) => {
    setPageSize(pageSize);
  };

  const handleGridSortModelChange = (model) => {
    setGridSortModel(model);
  };

  const guardedSetSelected = useCallback(
    (newSelection) => {
      if (JSON.stringify(newSelection) !== JSON.stringify(selected)) {
        setSelected(newSelection);
      }
    },
    [selected]
  );

  /** @type {import('../modules/Manage/pages/queues-table/DataTable').IColDef[]} */
  const columns = [
    {
      field: 'created',
      headerName: 'Created',
      /* eslint-disable no-multi-spaces */
      valueFormatter: (params) =>
        params.row.created // creaeted is coming directly from MySql datetime column so use fromSQL
          ? DateTime.fromSQL(params.row.created, { zone: 'UTC' }) // SQL datetime does not have timezone so we first must set to UTC
              .setZone(businessDataCached.current?.timezone_php) // On this line we now switch it to the business time zone
              .toLocaleString(DateTime.DATETIME_SHORT) // now we format to local datetime format
          : '-',
      /* eslint-enable no-multi-spaces */
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
    },
    {
      field: 'number',
      headerName: 'Number',
      enableSorting: false,
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      renderCell: phoneNumberformatter,
    },
    {
      field: 'inboundcalls',
      headerName: 'Inbound Calls',
      enableSorting: false,
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      renderCell: (params) => (params.row.inboundcalls ? <DoneIcon /> : ' '),
    },
    {
      field: 'outboundcalls',
      headerName: 'Outbound Calls',
      enableSorting: false,
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      renderCell: (params) => (params.row.outboundcalls ? <DoneIcon /> : ' '),
    },
    {
      field: 'inboundsms',
      headerName: 'Inbound SMS',
      enableSorting: false,
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      renderCell: (params) => (params.row.inboundsms ? <DoneIcon /> : ' '),
    },
    {
      field: 'outboundsms',
      headerName: 'Outbound SMS',
      enableSorting: false,
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      renderCell: (params) => (params.row.outboundsms ? <DoneIcon /> : ' '),
    },
    {
      field: 'id',
      enableSorting: false,
      headerName: 'Actions',
      headerAlign: 'center',
      align: 'center',
      width: 150,
      renderCell: ActionColumn({
        deleteItemId: deleteGroupId,
        setDeleteItemId: setDeleteGroupId,
        isLoading: deleteStatus === loadableEnum.LOADING,
        handleDeletion: handleDeleteBlockedNumber,
        editLink: '/manage/numbers/blocked/:id/edit',
      }),
    },
  ];
  const blockedData = useRecoilValueLoadable(
    getBlockedNumbers({ page, pageSize, sortModel, search })
  );
  const numbersCached = useRef([]);
  numbersCached.current = useMemo(
    () =>
      blockedData.state === 'hasValue'
        ? blockedData?.contents?.response?.data?.body?.results?.map((o) => ({
            ...o,
            // this is done since there is no unique id, for actions column it will extract this key/value
            id: `${o.number},${o.businessid}`,
          }))
        : numbersCached.current,
    [blockedData]
  );

  const ToolbarComponent = useCallback(
    (props) => (
      <EnhancedTableToolbar
        {...props}
        dataType="Blocked Numbers"
        numSelected={selected.length}
        search={search}
        setSearch={handleSearch}
        rightComponent={
          <ToolbarAddDeleteButton
            dataPup={'add-blocked-numbers'}
            numSelected={selected.length}
            onClickForAdd={() => {
              trackAddBlockedNumber();
              handleShowA();
            }}
            onClickForDelete={handleShow}
            isLoading={blockedData.state !== 'hasValue'}
          />
        }
      />
    ),
    [handleShowA, handleShow, selected.length]
  );
  return (
    <Paper>
      <Switch>
        <Route path="/manage/numbers/blocked/:id/edit">
          {({ history, match }) => (
            <EditBlockedNumberModal
              show={match != null}
              setRefetchToggleForBlockedNumbers={
                setRefetchToggleForBlockedNumbers
              }
              onHide={() => {
                history.push('/manage/numbers/blocked');
              }}
              numbers={numbersCached.current}
            />
          )}
        </Route>
      </Switch>
      <DataTable
        enableSelectAll={true}
        setSelected={guardedSetSelected}
        rows={numbersCached?.current ?? []}
        loading={blockedData.state === 'loading'}
        columns={columns}
        rowCount={blockedData?.contents?.response?.data?.body?.total}
        displayCount={blockedData?.contents?.response?.data?.body?.total}
        page={page}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        paginationMode="server"
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={handleGridSortModelChange}
        ToolbarComponent={ToolbarComponent}
        selected={selected}
      />
      <AddBlockedNumberModal
        setRefetchToggleForBlockedNumbers={setRefetchToggleForBlockedNumbers}
        show={showA}
        onHide={handleCloseA}
      />
      <DeleteBlockedNumbersModal
        setRefetchToggleForBlockedNumbers={setRefetchToggleForBlockedNumbers}
        enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
        selected={selected}
        userAtom={userAtom}
        setSelected={setSelected}
        handleClose={handleClose}
        show={show}
      />
    </Paper>
  );
}
